import I18n from 'i18n-js';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import React, { useState } from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import * as elog from '@reverbdotcom/commons/src/elog';
import windowWrapper from '@reverbdotcom/commons/src/window_wrapper';
import { RCCallout, UtilityVariant } from '@reverbdotcom/cadence/components';
import { ProtectionShieldIcon, TruckIcon } from '@reverbdotcom/cadence/icons/react';
import { NewspaperIcon } from '@phosphor-icons/react';

import { Paths } from './shared/url_helpers';

const SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID = '5440807127443';
const CALCULATED_SHIPPING_ARTICLE_ID = '41988368807451';

interface INagProps {
  taxType?: string;
}

interface IProps {
  nagId: string;
  nagProps?: INagProps;
}

const TRUST_MESSAGES_NAG = 'trust_messages_nag';
const TRUST_OFFERS_NAG = 'trust_offers_nag';
const USPS_APR_2026_PRICE_NAG = 'usps_apr_2026_price_nag';
const INTERNATIONAL_TAX_ID_NAG = 'international_tax_id_nag';

const USPS_APR_2026_EXTERNAL_LINK = `
https://about.usps.com/newsroom/national-releases/2026/0325-usps-announces-transportation-related-time-limited-price-change.htm
`;

const NAG_ID_TO_PARAMS: {
  [nagId: string]: {
    variant: UtilityVariant;
    icon: React.ComponentProps<typeof RCCallout>['icon'];
    nagComponent: React.ComponentType<INagProps>;
    ctaComponent?: React.ComponentType;
    compact?: boolean;
  };
} = {
  [TRUST_MESSAGES_NAG]: {
    variant: 'loud-ultralight',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [TRUST_OFFERS_NAG]: {
    variant: 'loud-ultralight',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [USPS_APR_2026_PRICE_NAG]: {
    variant: 'info-ultralight',
    icon: <TruckIcon />,
    nagComponent: UspsApr2026PriceNag,
  },
  [INTERNATIONAL_TAX_ID_NAG]: {
    variant: 'info-ultralight',
    icon: <NewspaperIcon />,
    nagComponent: InternationalTaxIdNag,
    compact: true,
  },
};

function SuspiciousActivityNag() {
  return (
    <div>
      <p className="mb-0">
        {I18n.t('discovery.dismissibleDashboardBanner.trustMessage')}
      </p>
      <CoreLink
        to={Paths.helpCenterArticlesUrl.expand({ articleId: SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID })}
        className="fg-color-text-primary td-underline"
        target="_blank"
      >
        {I18n.t('discovery.dismissibleDashboardBanner.trustLearnMore')}
      </CoreLink>
    </div>
  );
}

function UspsApr2026PriceNag() {
  return (
    <div className="ml-2">
      <Translate
        tag="span"
        text="discovery.dismissibleDashboardBanner.usps2026MessageOne"
      >
        <CoreLink
          to={USPS_APR_2026_EXTERNAL_LINK}
          target="_blank"
          className="mr-1 fg-color-text-primary td-underline"
        >
          <Translate text="discovery.dismissibleDashboardBanner.usps2026MessageOneLinkText" />
        </CoreLink>
      </Translate>
      <Translate
        tag="span"
        text="discovery.dismissibleDashboardBanner.usps2026MessageTwo"
      >
        <CoreLink
          to={Paths.helpCenterArticlesUrl.expand({ articleId: CALCULATED_SHIPPING_ARTICLE_ID })}
          target="_blank"
          className="fg-color-text-primary td-underline"
        >
          <Translate text="discovery.dismissibleDashboardBanner.usps2026MessageTwoLinkText" />
        </CoreLink>
      </Translate>
    </div>
  );
}

function InternationalTaxIdNag({ taxType = undefined }: INagProps) {
  if (!taxType) { return null; }

  return (
    <p>
      <Translate
        tag="span"
        text="discovery.internationalTaxIds.banner.addTaxIdText"
      >
        <strong>
          <Translate
            text="discovery.internationalTaxIds.banner.addTaxIdTitle"
            args={{ taxType: I18n.t(`discovery.internationalTaxIds.banner.${taxType}`) }}
          />
        </strong>
      </Translate>
      {' '}
      <CoreLink
        to="/my/selling/tax_policy"
        target="_blank"
        rel="noopener noreferrer"
        className="fg-color-text-primary td-underline"
      >
        {I18n.t('discovery.internationalTaxIds.banner.addTaxIdLinkText')}
      </CoreLink>
    </p>
  );
}

export function DismissibleDashboardBanner({ nagId, nagProps = {} }: IProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) { return null; }

  async function onDismiss() {
    try {
      await windowWrapper.jQuery.post(Paths.closeNagAlert.expand({ nagId }));
    } catch (e) {
      elog.error('discovery.dashboard_banner_dismiss.error', {}, e);
    }

    setIsDismissed(true);
  }

  // The backend picks nagId, and it can deploy before the frontend does. That means it can
  // ask for a nag this code doesn't have yet, so show nothing instead of crashing.
  const nagParams = NAG_ID_TO_PARAMS[nagId];

  if (!nagParams) { return null; }

  const NagComponent = nagParams.nagComponent;
  const CtaComponent = nagParams.ctaComponent;

  return (
    <div className="mt-2 mb-4">
      <RCCallout
        themeType="utility"
        variant={nagParams.variant}
        border="thin"
        icon={nagParams.icon}
        onDismiss={onDismiss}
        cta={CtaComponent ? <CtaComponent /> : undefined}
        compact={nagParams.compact ? true : undefined}
      >
        <NagComponent
          {...nagProps} // eslint-disable-line react/jsx-props-no-spreading
        />
      </RCCallout>
    </div>
  );
}

export default DismissibleDashboardBanner;
